import React, { SyntheticEvent } from 'react';
import { ProductAttribute_productAttribute_options, createProductAttributeOptionVariables, updateProductAttributeOptionVariables } from '@api/graphql/types';
import { compose } from 'react-apollo';
import { injectIntl, InjectedIntlProps } from 'react-intl';
import ProductAttributeOptionModal from './ProductAttributeOptionModal';
import * as Mutations from '@api/graphql/mutations';
import GraphQLClient from '@api/graphql/GraphQLClient';
import DeleteButton from '@components/DeleteButton';
import { ToastLevelType } from '@components/Toast/Toast';
import { ToastActionType } from '@actions/toastActions';
import { connect, DispatchProp } from 'react-redux';

type ProductAttributeOptionsProps = {
    name: string,
    producAttributeId: string,
    value: ProductAttribute_productAttribute_options[] | null,
    onChangeHandler: (value: any, name: string) => void,
    refetch?: () => void;
    [key: string]: any,
} & InjectedIntlProps & DispatchProp;

interface State {
    currentProdcutAttributeOption: updateProductAttributeOptionVariables | createProductAttributeOptionVariables;
    modalVisible: boolean;
}

const newOption: createProductAttributeOptionVariables = {
    label: '',
    is_active: true,
    is_default: false,
    product_attribute_id: '',
};

class ProductAttributeOptions extends React.Component<ProductAttributeOptionsProps, State> {

    public state = {
        modalVisible: false,
        currentProdcutAttributeOption: newOption,
    };

    private feedBack(key: string, type: ToastLevelType) {
        if (this.props.refetch) {
            this.props.refetch();
        }
        this.props.dispatch({type: ToastActionType.addToast, toast: {
            id: `${key}-${type}`,
            text: this.props.intl.formatMessage({id: `module.productAttribute.notification.${key}`}),
            type,
            timeout: 2000,
        }});
    }

    private deleteHandler = (option: ProductAttribute_productAttribute_options) => {
        GraphQLClient.mutate({
            mutation: Mutations.deleteProductAttributeOption, variables: {id: option.id},
        }).then(() => {
            this.feedBack('delete.success', ToastLevelType.success);
        }).catch(() => {
            this.feedBack('error', ToastLevelType.error);
        });
    }

    private addHandler = (option?: ProductAttribute_productAttribute_options) => (e: SyntheticEvent) => {
        e.preventDefault();
        this.setState({
            modalVisible: true,
            currentProdcutAttributeOption: {
                ...newOption,
                product_attribute_id: this.props.productAttributeId,
                after: option ? option.id : undefined,
            },
        });
    }

    private editHandler = (option: ProductAttribute_productAttribute_options) => (e: SyntheticEvent) => {
        e.preventDefault();
        const currentProdcutAttributeOption = {
            id: option.id,
            is_active: option.is_active,
            is_default: option.is_default,
            label: option.label,
            product_attribute_id: this.props.productAttributeId,
        };
        this.setState({modalVisible: true, currentProdcutAttributeOption});
    }

    public hideModal = () => {
        this.setState({modalVisible: false});
    }

    private actionsRenderer = (option: ProductAttribute_productAttribute_options) => {
        return (
            <div className="row">
                <div className="col-xs-6 text-center">
                    <button className='btn btn-info btn-xs' onClick={this.editHandler(option)}><i className="fa fa-edit" title={this.props.intl.formatMessage({id: 'common.edit'})} /></button>
                </div>
                <div className="col-xs-6 text-center">
                    <DeleteButton className='btn-xs' onClick={this.deleteHandler} value={option}>
                        <i className="fa fa-trash" title={this.props.intl.formatMessage({id: 'common.delete'})} />
                    </DeleteButton>
                </div>
            </div>
        );
    }

    private renderOption = (option: ProductAttribute_productAttribute_options, index: number) => {
        const moveUp = index !== 0 ?
            <a className="pull-left" onClick={this.moveProductAttributeOption(option, {before: this.props.value![(index - 1)].id})}><i className="fa fa-chevron-up" /></a> :
            '';

        const moveDown = index !== (this.props.value!.length - 1) ?
            <a className="pull-right" onClick={this.moveProductAttributeOption(option, {after: this.props.value![(index + 1)].id})}><i className="fa fa-chevron-down" /></a> :
            '';

        return (
            <tr key={option.id}>
                <td>{option.id}</td>
                <td>{option.label}</td>
                <td>{option.is_active ? this.props.intl.formatMessage({id: 'common.yes'}) : this.props.intl.formatMessage({id: 'common.no'})}</td>
                <td style={{width: '10px'}}>
                    {moveUp}
                    {moveDown}
                </td>
                <td>
                    {this.actionsRenderer(option)}
                </td>
            </tr>
        );
    }

    private renderAddButton = () => {
        return (
            <button className='btn btn-primary btn-sm' onClick={this.addHandler()}><i className="fa fa-plus" title="Beszúrás" /> {this.props.intl.formatMessage({id: 'module.productAttribute.newOption'})}</button>
        );
    }

    private moveProductAttributeOption = (a: ProductAttribute_productAttribute_options, position: {before?: string, after?: string}) => (e: SyntheticEvent) => {
        GraphQLClient.mutate({
            mutation: Mutations.updateProductAttributeOption, variables: {...a, ...position, product_attribute_id: this.props.productAttributeId},
        }).then(() => {
            this.feedBack('move.success', ToastLevelType.success);
        }).catch(() => {
            this.feedBack('error', ToastLevelType.error);
        });
    }

    private renderTable() {
        if (this.props.value && this.props.value.length) {
            return (
                <>
                    <div className="row">
                        <div className="col-sm-12">
                            <div className="pull-right">
                                {this.renderAddButton()}
                            </div>
                        </div>
                    </div>
                    <table className="table table-striped">
                        <thead>
                            <tr>
                                <th>{this.props.intl.formatMessage({ id: 'module.productAttribute.id.title' })}</th>
                                <th>{this.props.intl.formatMessage({ id: 'module.productAttribute.label.title' })}</th>
                                <th>{this.props.intl.formatMessage({ id: 'module.productAttribute.is_active.title' })}</th>
                                <th>{this.props.intl.formatMessage({ id: 'common.sort' })}</th>
                                <th>&nbsp;</th>
                            </tr>
                        </thead>
                        <tbody>
                            {this.props.value.map(this.renderOption)}
                        </tbody>
                    </table>
                </>
            );
        }
        return this.renderAddButton();
    }

    public render() {
        return (
            <>
                <ProductAttributeOptionModal
                    visible={this.state.modalVisible}
                    onClose={() => {
                        if (this.props.refetch) {
                            this.props.refetch();
                        }
                        this.setState({modalVisible: false});
                    }}
                    data={this.state.currentProdcutAttributeOption}
                />
                {this.renderTable()}
            </>
        );
    }
}

export default compose(
    injectIntl,
    connect(null),
)(ProductAttributeOptions);



// WEBPACK FOOTER //
// ./src/modules/ProductAttribute/components/ProductAttributeOptions.tsx